import { useState, useEffect, useMemo } from "react";
import Footer from "./Footer";
import config from "../config";

const { API_BASE, UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json();
}

function getGalleryImages(product) {
  return [
    product.image_filename, product.image_filename2, product.image_filename3,
  ].filter(Boolean);
}

function getDescriptionLines(description = "") {
  return description
    .split("\n")
    .map((line) => line.replace(/^\s*-\s*/, "").trim())
    .filter(Boolean);
}

/* ── Lightbox ── */
function Lightbox({ images, index, title, onClose, onNavigate }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNavigate(1);
      if (e.key === "ArrowLeft") onNavigate(-1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, onNavigate]);

  return (
    <div className="pr-lightbox" onClick={onClose} role="dialog" aria-modal="true">
      <button className="pr-lightbox__close" onClick={onClose} aria-label="Close">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
          <path d="M4 4l12 12M16 4L4 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      {images.length > 1 && (
        <button
          className="pr-lightbox__nav pr-lightbox__nav--prev"
          onClick={(e) => { e.stopPropagation(); onNavigate(-1); }}
          aria-label="Previous image"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12 4l-6 6 6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <img
        src={`${UPLOADS_URL}/products/${images[index]}`}
        alt={`${title} – ${index + 1}`}
        className="pr-lightbox__img"
        onClick={(e) => e.stopPropagation()}
      />

      {images.length > 1 && (
        <button
          className="pr-lightbox__nav pr-lightbox__nav--next"
          onClick={(e) => { e.stopPropagation(); onNavigate(1); }}
          aria-label="Next image"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M8 4l6 6-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <div className="pr-lightbox__footer">
        {title && <span className="pr-lightbox__title">{title}</span>}
        {images.length > 1 && (
          <span className="pr-lightbox__counter">{index + 1} / {images.length}</span>
        )}
      </div>
    </div>
  );
}

/* ── Product card ── */
function ProductCard({ product, onOpenGallery }) {
  const gallery = getGalleryImages(product);
  const hasGallery = gallery.length > 0;
  const descLines = getDescriptionLines(product.description);
  const brochureUrl = product.brochure_url || null;

  return (
    <div className="pr-card">
      <button
        type="button"
        className="pr-card__media"
        onClick={() => hasGallery && onOpenGallery(gallery, product.title)}
        aria-label={`View images for ${product.title}`}
        disabled={!hasGallery}
      >
        {product.image_filename ? (
          <img
            src={`${UPLOADS_URL}/products/${product.image_filename}`}
            alt={product.title}
            className="pr-card__img"
          />
        ) : (
          <div className="pr-card__img-fallback" />
        )}

        <div className="pr-card__scrim" aria-hidden="true" />

        {hasGallery && gallery.length > 1 && (
          <span className="pr-card__photos">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M2 3a1 1 0 0 1 1-1h1.5l.7-1h5.6l.7 1H13a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3Zm6 2.2a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" />
            </svg>
            {gallery.length} PHOTOS
          </span>
        )}

        <div className="pr-card__text">
          <div className="pr-card__title-row">
            <h3 className="pr-card__title">
              <span className="pr-card__title-bar" aria-hidden="true" />
              {product.title}
            </h3>
            {product.supplier && (
              <span className="pr-card__supplier">{product.supplier}</span>
            )}
          </div>
          {descLines.length > 0 && (
            <ul className="pr-card__desc">
              {descLines.map((line, i) => (
                <li key={i} className="pr-card__desc-item">{line}</li>
              ))}
            </ul>
          )}
        </div>
      </button>

      {brochureUrl && (
        <div className="pr-card__brochure">
          <a
            href={brochureUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="pr-card__brochure-btn pr-card__brochure-btn--view"
            aria-label={`View brochure for ${product.title}`}
          >
            <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M10 4.5C6 4.5 2.5 10 2.5 10S6 15.5 10 15.5 17.5 10 17.5 10 14 4.5 10 4.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
              <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.6"/>
            </svg>
            View Brochure
          </a>
          <a
            href={brochureUrl}
            download
            className="pr-card__brochure-btn pr-card__brochure-btn--download"
            aria-label={`Download brochure for ${product.title}`}
          >
            <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M10 3v10m0 0-3.5-3.5M10 13l3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 15.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
            </svg>
            Download
          </a>
        </div>
      )}
    </div>
  );
}

/* ── Main Page ── */
function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchJSON(`${API_BASE}/products.php`)
      .then((data) => {
        if (cancelled) return;
        setProducts(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("ProductsPage load error:", err);
        setError("Failed to load products.");
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  /* Build ordered tab list: "All" first, then suppliers sorted alphabetically */
  const suppliers = useMemo(() => {
    const names = [...new Set(
      products.map((p) => p.supplier).filter(Boolean)
    )].sort((a, b) => a.localeCompare(b));
    return names;
  }, [products]);

  const tabs = useMemo(() => {
    return suppliers.length > 0 ? ["All", ...suppliers] : [];
  }, [suppliers]);

  /* Products visible in the current tab */
  const visibleProducts = useMemo(() => {
    if (activeTab === "All") return products;
    return products.filter((p) => p.supplier === activeTab);
  }, [products, activeTab]);

  const openGallery = (images, title) => setLightbox({ images, title, index: 0 });
  const closeGallery = () => setLightbox(null);
  const navigateGallery = (dir) => {
    setLightbox((lb) => lb && ({
      ...lb,
      index: (lb.index + dir + lb.images.length) % lb.images.length,
    }));
  };

  /* Per-tab product counts shown in the tab badge */
  const countForTab = (tab) => {
    if (tab === "All") return products.length;
    return products.filter((p) => p.supplier === tab).length;
  };

  return (
    <>
      <div className="pr">

        {/* ── HERO ── */}
        <div className="pr-hero">
          <img
            src={`${UPLOADS_URL}/hero/hero-02.png`}
            alt=""
            aria-hidden="true"
            className="pr-hero__img"
          />
          <div className="pr-hero__overlay" />
          <div className="pr-hero__content">
            <nav className="pr-bread" aria-label="Breadcrumb">
              <a href="/" className="pr-bread__link">Home</a>
              <span className="pr-bread__sep" aria-hidden="true">›</span>
              <span className="pr-bread__current">Products</span>
            </nav>
            <h1 className="pr-hero__title">Our Products</h1>
            <p className="pr-hero__subtitle">Explore the solutions powering our service delivery</p>
          </div>
        </div>

        {/* ── SUPPLIER TABS ── */}
        {!loading && !error && tabs.length > 1 && (
          <div className="pr-tabs-wrap">
            <div className="pr-tabs-inner">
              <div className="pr-tabs" role="tablist" aria-label="Filter by supplier">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab}
                    className={`pr-tab ${activeTab === tab ? "pr-tab--active" : ""}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                    <span className="pr-tab__count">{countForTab(tab)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── GRID ── */}
        <div className="pr-body">
          <div className="pr-body__inner">

            {loading && <div className="pr-state">Loading products…</div>}

            {!loading && error && (
              <div className="pr-state pr-state--error">{error}</div>
            )}

            {!loading && !error && visibleProducts.length === 0 && (
              <div className="pr-state">No products available yet.</div>
            )}

            {!loading && !error && visibleProducts.length > 0 && (
              <div className="pr-grid">
                {visibleProducts.map((p) => (
                  <ProductCard key={p.id} product={p} onOpenGallery={openGallery} />
                ))}
              </div>
            )}

          </div>
        </div>

        {lightbox && (
          <Lightbox
            images={lightbox.images}
            index={lightbox.index}
            title={lightbox.title}
            onClose={closeGallery}
            onNavigate={navigateGallery}
          />
        )}

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800;900&display=swap');

          .pr *, .pr *::before, .pr *::after { box-sizing: border-box; margin: 0; padding: 0; }
          .pr a { text-decoration: none; }
          .pr { font-family: 'Roboto', sans-serif; background: #f5f7fa; }

          /* ── HERO ── */
          .pr-hero { position: relative; height: 260px; overflow: hidden; background: #013d97; }
          .pr-hero__img { width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
          .pr-hero__overlay {
            position: absolute; inset: 0;
            background: linear-gradient(135deg, rgba(1,21,70,.88) 0%, rgba(1,61,151,.7) 70%, rgba(2,154,221,.55) 140%);
          }
          .pr-hero__content {
            position: absolute; inset: 0;
            display: flex; flex-direction: column; justify-content: flex-end;
            padding: 32px 40px; max-width: 1280px; margin: 0 auto; left: 0; right: 0;
          }
          .pr-bread { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 14px; }
          .pr-bread__link { font-size: 13px; font-weight: 600; color: rgba(255,255,255,.85); background: rgba(255,255,255,.12); padding: 3px 10px; border-radius: 3px; transition: background .2s; }
          .pr-bread__link:hover { background: rgba(255,255,255,.22); }
          .pr-bread__sep { color: rgba(255,255,255,.55); font-size: 14px; }
          .pr-bread__current { font-size: 13px; color: rgba(255,255,255,.7); }
          .pr-hero__title { font-size: clamp(26px, 3.2vw, 40px); font-weight: 900; color: #fff; line-height: 1.15; margin-bottom: 10px; }
          .pr-hero__subtitle { font-size: 15px; color: rgba(255,255,255,.78); max-width: 540px; line-height: 1.6; }

          /* ── SUPPLIER TABS ── */
          .pr-tabs-wrap {
            background: #fff;
            border-bottom: 1px solid #e2e8f0;
            box-shadow: 0 2px 8px rgba(1,21,70,.06);
            position: sticky;
            top: 96px; /* matches navbar height */
            z-index: 100;
          }
          .pr-tabs-inner {
            max-width: 1280px; margin: 0 auto; padding: 0 40px;
            overflow-x: auto;
            scrollbar-width: none;
          }
          .pr-tabs-inner::-webkit-scrollbar { display: none; }
          .pr-tabs {
            display: flex;
            gap: 4px;
            padding: 10px 0;
            white-space: nowrap;
          }
          .pr-tab {
            display: inline-flex; align-items: center; gap: 8px;
            padding: 8px 18px;
            background: none; border: 1.5px solid transparent;
            border-radius: 6px; cursor: pointer;
            font-family: 'Roboto', sans-serif;
            font-size: 13.5px; font-weight: 600; color: #5a6478;
            transition: color .18s, background .18s, border-color .18s;
            white-space: nowrap;
          }
          .pr-tab:hover {
            color: #013d97;
            background: #eef3fb;
            border-color: #c8d8f5;
          }
          .pr-tab--active {
            color: #fff;
            background: #013d97;
            border-color: #013d97;
          }
          .pr-tab--active:hover {
            background: #022d75;
            border-color: #022d75;
            color: #fff;
          }
          .pr-tab__count {
            display: inline-flex; align-items: center; justify-content: center;
            min-width: 20px; height: 20px; padding: 0 6px;
            border-radius: 10px;
            font-size: 11px; font-weight: 800; letter-spacing: 0;
            background: rgba(1,61,151,.12); color: #013d97;
            transition: background .18s, color .18s;
          }
          .pr-tab--active .pr-tab__count {
            background: rgba(255,255,255,.22);
            color: #fff;
          }

          /* ── BODY ── */
          .pr-body { padding: 56px 0 80px; }
          .pr-body__inner { max-width: 1280px; margin: 0 auto; padding: 0 40px; }

          .pr-state {
            text-align: center; padding: 64px 24px; font-size: 14px;
            color: #8890a0; background: #fff; box-shadow: 0 2px 12px rgba(0,0,0,.06);
          }
          .pr-state--error { color: #c0392b; }

          /* ── GRID ── */
          .pr-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 28px;
          }

          /* ── CARD ── */
          .pr-card {
            border-radius: 8px; overflow: hidden;
            box-shadow: 0 4px 18px rgba(1,21,70,.1);
          }
          .pr-card__media {
            position: relative; display: block; width: 100%; aspect-ratio: 3/4;
            border: none; padding: 0; cursor: zoom-in; background: #1a1a2e; overflow: hidden;
          }
          .pr-card__media:disabled { cursor: default; }
          .pr-card__img {
            position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover;
            transition: transform .5s ease;
          }
          .pr-card__media:hover:not(:disabled) .pr-card__img { transform: scale(1.06); }
          .pr-card__img-fallback {
            position: absolute; inset: 0;
            background: linear-gradient(135deg, #013d97, #029add);
          }

          /*
            Base scrim: dark gradient only, just enough at the bottom
            for the title to stay readable. No blue tint and no
            description visible until hovered (this rule now applies
            identically on all screen sizes — see RESPONSIVE section,
            which no longer overrides it).
          */
          .pr-card__scrim {
            position: absolute; inset: 0;
            background: linear-gradient(to top, rgba(2,4,12,.92) 0%, rgba(2,4,12,.55) 28%, rgba(2,4,12,0) 60%);
            transition: background .35s ease;
          }

          /*
            Hover scrim: darker dark-gradient (for legibility behind the
            now-visible description) layered with a transparent blue
            tint on top, for both desktop hover and mobile tap (touch
            devices trigger :hover on tap).
          */
          .pr-card__media:hover:not(:disabled) .pr-card__scrim {
            background:
              linear-gradient(to top, rgba(2,4,12,.95) 0%, rgba(2,4,12,.78) 45%, rgba(2,4,12,.15) 85%),
              rgba(2,154,221,.22);
          }

          .pr-card__photos {
            position: absolute; top: 14px; right: 14px;
            display: flex; align-items: center; gap: 6px;
            background: rgba(2,154,221,.92); color: #fff; border-radius: 3px;
            font-size: 11px; font-weight: 800; letter-spacing: .04em;
            padding: 6px 11px;
          }
          .pr-card__text {
            position: absolute; left: 0; right: 0; bottom: 0;
            padding: 24px 26px 26px;
          }
          .pr-card__title-row {
            display: flex; align-items: flex-start; justify-content: space-between;
            gap: 14px;
          }
          .pr-card__title {
            display: flex; align-items: flex-start; gap: 12px;
            font-size: 21px; font-weight: 800; color: #fff; line-height: 1.28;
            min-width: 0;
          }
          .pr-card__title-bar {
            flex-shrink: 0; width: 3px; align-self: stretch; min-height: 26px;
            background: linear-gradient(180deg, #029add 0%, #7f5af0 100%);
            border-radius: 2px; margin-top: 2px;
          }
          .pr-card__supplier {
            flex-shrink: 0; margin-top: 3px;
            background: rgba(255,255,255,.14);
            border: 1px solid rgba(255,255,255,.28);
            color: #fff;
            font-size: 10.5px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase;
            padding: 5px 10px; border-radius: 20px;
            white-space: nowrap;
          }

          /*
            Description: hidden by default (max-height: 0 + opacity: 0),
            revealed only on hover/tap via the rule below. This now
            applies on every screen size — the old mobile override that
            forced it open has been removed.
          */
          .pr-card__desc {
            list-style: none;
            font-size: 13px; line-height: 1.85; color: rgba(255,255,255,.82);
            margin: 14px 0 0 15px;
            max-height: 0; opacity: 0;
            overflow: hidden;
            transition: max-height .45s ease, opacity .3s ease, margin .45s ease;
          }
          .pr-card__media:hover:not(:disabled) .pr-card__desc {
            max-height: 260px; opacity: 1;
          }
          .pr-card__desc-item {
            position: relative; padding-left: 14px;
          }
          .pr-card__desc-item::before {
            content: "–"; position: absolute; left: 0; top: 0;
            color: #029add; font-weight: 700;
          }

          /* ── BROCHURE BAR ── */
          .pr-card__brochure {
            display: flex;
            background: #fff;
            border-top: 1px solid #e8ecf2;
          }
          .pr-card__brochure-btn {
            flex: 1; display: flex; align-items: center; justify-content: center; gap: 7px;
            padding: 13px 10px; font-size: 12.5px; font-weight: 700; letter-spacing: .03em;
            text-decoration: none; transition: background .2s, color .2s;
            border: none; cursor: pointer; font-family: 'Roboto', sans-serif;
          }
          .pr-card__brochure-btn--view { color: #013d97; background: #fff; border-right: 1px solid #e8ecf2; }
          .pr-card__brochure-btn--view:hover { background: #eef3fb; color: #011f5e; }
          .pr-card__brochure-btn--download { color: #fff; background: #013d97; }
          .pr-card__brochure-btn--download:hover { background: #022d75; }

          /* ── LIGHTBOX ── */
          .pr-lightbox {
            position: fixed; inset: 0; z-index: 1000;
            background: rgba(5,10,25,.92);
            display: flex; align-items: center; justify-content: center; padding: 40px;
          }
          .pr-lightbox__img {
            max-width: min(90vw, 1100px); max-height: 82vh;
            object-fit: contain; border-radius: 4px;
            box-shadow: 0 10px 40px rgba(0,0,0,.5);
          }
          .pr-lightbox__close {
            position: absolute; top: 20px; right: 20px;
            width: 40px; height: 40px; border-radius: 50%;
            background: rgba(255,255,255,.12); border: none; cursor: pointer;
            display: flex; align-items: center; justify-content: center;
            transition: background .2s;
          }
          .pr-lightbox__close:hover { background: rgba(255,255,255,.25); }
          .pr-lightbox__nav {
            position: absolute; top: 50%; transform: translateY(-50%);
            width: 48px; height: 48px; border-radius: 50%;
            background: rgba(255,255,255,.12); border: none; cursor: pointer;
            display: flex; align-items: center; justify-content: center;
            transition: background .2s;
          }
          .pr-lightbox__nav:hover { background: rgba(255,255,255,.25); }
          .pr-lightbox__nav--prev { left: 20px; }
          .pr-lightbox__nav--next { right: 20px; }
          .pr-lightbox__footer {
            position: absolute; bottom: 24px; left: 50%; transform: translateX(-50%);
            display: flex; align-items: center; gap: 14px;
            color: #fff; font-size: 13px;
          }
          .pr-lightbox__title { font-weight: 700; }
          .pr-lightbox__counter { background: rgba(255,255,255,.14); padding: 6px 14px; border-radius: 20px; }

          /* ── RESPONSIVE ── */
          @media (max-width: 1024px) {
            .pr-grid { grid-template-columns: repeat(2, 1fr); }
          }
          @media (max-width: 768px) {
            .pr-hero { height: 200px; }
            .pr-hero__content { padding: 20px; }
            .pr-body { padding: 40px 0 64px; }
            .pr-body__inner { padding: 0 20px; }
            .pr-grid { grid-template-columns: 1fr; gap: 20px; }
            .pr-card__media { aspect-ratio: 4/3; }
            .pr-lightbox { padding: 20px; }
            .pr-lightbox__nav { width: 40px; height: 40px; }
            .pr-tabs-wrap { top: 80px; }
            .pr-tabs-inner { padding: 0 20px; }
          }
          @media (max-width: 480px) {
            .pr-tabs-wrap { top: 70px; }
          }

          a:focus-visible, button:focus-visible { outline: 2px solid #029add; outline-offset: 2px; }
          @media (prefers-reduced-motion: reduce) {
            .pr *, .pr *::before, .pr *::after { transition-duration: 0ms !important; }
          }
        `}</style>
      </div>
      <Footer />
    </>
  );
}

export default ProductsPage;